import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { PageToolbar, UnsplashCredit, usePageTranslation } from "@/components/PageToolbar";

export default function ArticleDetail() {
  const { id } = useParams();
  const [article, setArticle] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    setArticle(null);
    api.get(`/articles/${id}`).then((r) => setArticle(r.data));
  }, [id]);

  const blocks = useMemo(() => {
    if (!article) return null;
    const b = { title: article.title, excerpt: article.excerpt };
    article.body.split("\n\n").forEach((p, i) => (b[`p${i}`] = p));
    return b;
  }, [article]);
  const { lang, tr, busy } = usePageTranslation(blocks, `article-${id}`);

  if (!article) return <main className="max-w-7xl mx-auto px-6 py-24 text-center font-display text-2xl text-[#6b5d51]">Loading…</main>;

  const paragraphs = article.body.split("\n\n");
  const speakText = [tr("title", article.title), ...paragraphs.map((p, i) => tr(`p${i}`, p))].join(" ");

  return (
    <main data-testid="article-detail-page" className="max-w-3xl mx-auto px-4 sm:px-6 py-12">
      <Link to="/news" data-testid="back-to-news-link" className="text-sm font-medium link-underline text-[#7d1a1a]">← All News</Link>
      <div className="flex items-center gap-3 mt-6 flex-wrap">
        <span className="bg-[#7d1a1a] text-[#f6f1e7] font-mono-label text-[10px] tracking-[0.18em] uppercase px-2 py-1">{article.category}</span>
        {article.ai_generated && (
          <span data-testid="article-ai-badge" className="font-mono-label text-[9px] tracking-[0.12em] uppercase text-[#b06810]">✦ AI-assisted · Editor reviewed</span>
        )}
        <span className="font-mono-label text-[10px] tracking-wide text-[#8a7c6d]">{article.date} · By {article.author}</span>
      </div>
      <h1 data-testid="article-title" className="font-display font-bold text-3xl sm:text-5xl leading-tight mt-4 text-[#1c1512]">{tr("title", article.title)}</h1>
      <p className="text-xl text-[#4a3f35] mt-4 leading-relaxed italic">{tr("excerpt", article.excerpt)}</p>

      <div className="mt-6">
        <PageToolbar speakText={speakText} busy={busy} lang={lang} />
      </div>

      {article.photo && (
        <figure className="mt-8">
          <div className="aspect-[16/9] overflow-hidden bg-[#171210]">
            <img src={article.photo.url} alt={article.photo.alt} className="w-full h-full object-cover" />
          </div>
          <UnsplashCredit photo={article.photo} className="mt-2" />
        </figure>
      )}

      <article data-testid="article-body" className="mt-8 space-y-6">
        {paragraphs.map((p, i) => (
          <p key={i} className="text-lg leading-relaxed text-[#2b241d]">{tr(`p${i}`, p)}</p>
        ))}
      </article>

      <div className="flex flex-wrap gap-2 mt-10 rule-thick pt-6">
        {article.tags.map((t) => (
          <Link key={t} to={`/news?tag=${encodeURIComponent(t)}`} data-testid={`article-tag-${t.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="bg-[#efe7d7] border border-[#d8ccb8] rounded-full px-3 py-1 text-sm hover:border-[#7d1a1a] hover:text-[#7d1a1a] transition-colors">
            {t}
          </Link>
        ))}
      </div>
    </main>
  );
}
